// Copyright (c) 2017 Comsearch, Inc., All rights reserved

import { initialize } from 'redux-form'
import { fetchPathById } from 'helpers/api'
import { cleanAllComments } from 'redux/modules/databaseDetails'
import { loadingToggler } from 'redux/modules/loading'
import { getPathSettings } from 'redux/modules/domain'

import { PathStatusIdEnum } from 'config/enums'

import {
  assignPathPlacement,
  canUserEditPathDetails,
  hideAdminFieldsForThisPath,
  sendUserBackAndProvideWarning,
} from './helpers'

import {
  assignPathData,
  getPathMetaInfo,
  getDefaultRadioServiceIdForProject,
  makeInitialFormValues,
  makeInitialPassiveValues,
  pathDetailsError,
  prepPathDetailsForForm,
  updatePassiveRepeaterStore,
} from 'redux/modules/pathDetails'

// When a path details form mounts:
// - Get the project info for the path**
// - GET the PathDetailDTO from the API
// - scrub API data
// - initialize the redux-form state with the defined values
// - assign the path's meta information to redux state

// TODO - should this verify that if withinProjectSummary,
// the project id defined in projectSummary is the same as the API response?
// User could manually change URL and access a path's details from project X
// while projectSummary is talking about project Y.

const setCurrentPath = (pathIdStr, withinProjectSummary, sourceForm) => (dispatch, getState) => (
  dispatch(loadingToggler(
    async () => {
      const {
        projectSummary,
        user,
      } = getState()

      // first off, let's verify that there aren't any settings we need to load up before
      // parsing and rendering this data
      await dispatch(getPathSettings())

      const paths = projectSummary.get('paths')

      // tracks whether or not this path details is for a brand new path
      // only set to false at the end of try
      // used for special handling near the end of the thunk
      let isNewPath = true

      // these values are from the user's and the project's settings
      // if we're in the path DB, then there's no project and
      // those values are assigned defaults
      const {
        company,
        contact,
        domainId,
        pathPlacement,
        projectId,
        projectTypeId,
        unitType,
      } = getPathMetaInfo(withinProjectSummary, projectSummary, user)

      assignPathPlacement(paths, pathPlacement, pathIdStr)

      let pathDataNew
      let pathDetailsErrorMessage = ''
      let createUserId = null

      const comments = []

      try {
        // if it's called 'new-path', there's no reason to hit the API.
        // but we want to treat it the same as an API error
        // because we'll show new form values for both scenarios.
        if (pathIdStr === 'new-path') {
          const err = {}
          throw err
        }

        const { entity } = await fetchPathById(pathIdStr)

        if (
          // check to verify that if we're within project summary,
          // ensure that the path we're trying to look at is a child of this project.
          withinProjectSummary &&
          entity.projectId !== projectSummary.get('projectId')
        ) {
          // if we're within projectSummary and this path is not a child of the project,
          // send them back one page and warn them what happened.
          const message = `Path Id ${pathIdStr} does not belong to Project ${projectSummary.get('projectName')}`
          return dispatch(sendUserBackAndProvideWarning(message))

        // else if we are in the Path Database subtree and we're trying to look at an In Progress Path,
        // send them back in browser history and tell them why.
        } else if (
          !withinProjectSummary &&
          entity.pathStatus.pathStatusId === PathStatusIdEnum.IN_PROGRESS
        ) {
          // if we're within projectSummary and this path is not a child of the project,
          // send them back one page and warn them what happened.
          const message = `In Progress paths can only be accessed through their Project. Path Id ${pathIdStr} is In Progress and belongs to Project Id ${entity.projectId}`
          return dispatch(sendUserBackAndProvideWarning(message))
        }

        // legacy paths don't necessarily have a create user
        if (entity.createUser) {
          createUserId = entity.createUser.userId
        }

        pathDataNew = prepPathDetailsForForm(entity, unitType, domainId)

        // if there are no passives for this path, the passive repeater's store
        // will automatically empty old data. otherwise, it will populate with the
        // path's passive data
        dispatch(updatePassiveRepeaterStore(pathDataNew.passiveRepeaters))

        // pass initial data set for passive repeater
        // if there isn't a passive repeater in the path's data.
        if (!pathDataNew.passiveRepeaters.length) {
          pathDataNew.passiveRepeaters.push(makeInitialPassiveValues(unitType))
        }

        // Scrubs the comments just the same as Antenna/Radio comments
        const cleanedComments = cleanAllComments(entity.comments, 'pathCommentId')
        comments.push(...cleanedComments)

        // set this so even if a user can't edit the form, they can still at least read it.
        isNewPath = false
      } catch (err) {
        // if the path id threw an error from the API, display it in UI
        if (err.message) {
          pathDetailsErrorMessage = err.message
          console.warn(err)
        }

        // clears passive repeater's store - clears any persisting data
        dispatch(updatePassiveRepeaterStore())

        // if we're here that means that we're prepping for brand new path details form.
        // if this user is the project's create user,
        // then they have permission to create a new path if they so desire.
        // NOTE - this does not allow for anyone to create a path within the Database Management subtree.
        // If that is desired, this must be updated and the check which dispatches a warning() will
        // need to be updated accordingly.
        if (projectSummary.getIn(['createUser', 'userId']) === user.get('userId')) {
          createUserId = user.get('userId')
        }

        // NOTE - at the time of writing this, the only time you could create a new path was through project summary
        // so to be here, it's just assumed that we're in project summary.
        const radioServiceId = dispatch(getDefaultRadioServiceIdForProject())

        // initialize all form values with defaults
        // new paths default to path status of In Progress.
        const config = {
          company,
          contact,
          domainId,
          pathStatusId: PathStatusIdEnum.IN_PROGRESS,
          radioServiceId,
          unitType,
        }

        pathDataNew = makeInitialFormValues(config)
      }

      // because of how the path-details path info section (previously path header) lays out
      // it is easier to set all these values through redux-form in place of having custom components.
      pathDataNew.pathIndex = pathPlacement.pathIndex

      // both redux-form as well as pathDetails redux state should know what the path id is
      // and the deleted flag.
      const {
        deleted,
        pathId,
      } = pathDataNew

      // determine if the user can edit this path.
      const userCanEdit = dispatch(canUserEditPathDetails(pathDataNew, createUserId))

      // if this data is for a new path AND :
      // user cannot edit this path OR
      // it's not within the project summary subtree (only there can you create new paths)
      // send that user back to the parent page and display
      // NOTE - if you want to enable path creation through the database management subtree,
      // adjust this code below. You'll also need to update above in this file (inside of the catch)
      if (isNewPath && (!userCanEdit || !withinProjectSummary)) {
        // pass any possible error from the API to be displayed in the UI here.
        const message = (
          !withinProjectSummary
            ? pathDetailsErrorMessage || 'New paths must be created from within a project'
            : 'Your user permissions do not allow you to create a new path here.'
        )
        return dispatch(sendUserBackAndProvideWarning(message))
      }

      // Assign this value to redux-form's initial values so that
      // it knows to not validate the admin fields for
      // In Progress Paths on Engineering Projects.
      // See this form's sync validation handler for how this works.
      pathDataNew.hideAdminFields = hideAdminFieldsForThisPath(projectTypeId, pathDataNew.pathStatusId)

      // information about this path which supports features outside of the form itself.
      const pathInfo = {
        ...pathPlacement,
        deleted,
        defaultCompany: company,
        defaultContact: contact,
        pathId,
        projectId,
        // sourceForm is needed to properly capture a nuanced
        // passive repeater validation step on submit handling
        sourceForm,
        unitType,
        userCanEdit,
        withinBulkEdit: false,
        withinProjectSummary,
      }

      dispatch(assignPathData(pathInfo, comments))
      dispatch(pathDetailsError(pathDetailsErrorMessage))
      dispatch(initialize(sourceForm, pathDataNew))
    })
  )
)

export default setCurrentPath
